import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  Briefcase,
  DollarSign,
  MapPin,
  PieChart as PieIcon,
  BarChart3,
  Search,
  ArrowRight
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie
} from 'recharts';
import DataToolTip from '@/components/common/DataToolTip';
import axiosInstance from '@/services/axiosInstance';
import RegionalOccupationModal from '@/components/modals/RegionalOccupationModal';
import RegionalMarketDetailModal from '@/components/modals/RegionalMarketDetailModal';
import './styles.scss';

const JobMarketInsights = ({ institution = '' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeMetric, setActiveMetric] = useState('salary'); // salary or demand
  const [selectedArea, setSelectedArea] = useState((institution && institution.includes('Memphis')) ? 'Memphis, TN-MS-AR' : '');
  const [availableAreas, setAvailableAreas] = useState([]);
  const [isRegionalModalOpen, setIsRegionalModalOpen] = useState(false);
  const [selectedOcc, setSelectedOcc] = useState(null);
  const [isOccModalOpen, setIsOccModalOpen] = useState(false);

  useEffect(() => {
    fetchMarketData(selectedArea);
  }, [institution, selectedArea]);

  const fetchMarketData = async (area) => {
    setLoading(true);
    try {
      // Fetch specific analytics for the selected area
      // The backend now always returns the FULL areaBreakdown regardless of the regional filter,
      // enabling us to populate the dropdown from a single request.
      let url = '/api/datasets/msa/analytics?year=2025';
      if (area) {
        url += `&areaName=${encodeURIComponent(area)}`;
      }
      const response = await axiosInstance.get(url);
      setData(response.data);

      // Extract available areas from breakdown (now always full list)
      if (response.data.areaBreakdown) {
        const areas = response.data.areaBreakdown
          .map(a => a.name)
          .filter(n => n && n !== 'Undistributed Jurisdictions');

        setAvailableAreas(areas);
      }
    } catch (error) {
      console.error('Failed to fetch job market data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTopOccupations = () => {
    if (!data?.registry) return [];

    return [...data.registry]
      .filter(occ =>
        (occ.occTitle || occ.occ_title || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        if (activeMetric === 'salary') {
          const valA = a.annualMedianWage || a.annual_median || 0;
          const valB = b.annualMedianWage || b.annual_median || 0;
          return valB - valA;
        } else {
          const valA = a.totalEmployment || a.tot_emp || 0;
          const valB = b.totalEmployment || b.tot_emp || 0;
          return valB - valA;
        }
      });
  };

  const COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f59e0b', '#10b981', '#06b6d4', '#3b82f6'];

  if (loading) {
    return (
      <div className="market-insights-loading">
        <div className="pulse-loader"></div>
        <p>Analyzing Workforce Intelligence...</p>
      </div>
    );
  }

  const occupations = getTopOccupations();
  const chartData = occupations.map(occ => ({
    name: (occ.occTitle || occ.occ_title || 'Unknown').replace('Occupations', '').replace('Managers', 'Mgmt').substring(0, 25),
    salary: occ.annualMedianWage || occ.annual_median || 0,
    employment: occ.totalEmployment || occ.tot_emp || 0,
    fullTitle: occ.occTitle || occ.occ_title
  }));

  const formatCurrency = (val) => {
    if (!val) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(val);
  };

  const formatNumber = (val) => {
    if (!val) return '0';
    return new Intl.NumberFormat('en-US').format(val);
  };

  return (
    <div className="job-market-insights">
      <RegionalOccupationModal
        isOpen={isRegionalModalOpen}
        onClose={() => setIsRegionalModalOpen(false)}
        areaName={selectedArea}
        datasetType="msa"
        year="2025"
      />

      <RegionalMarketDetailModal
        isOpen={isOccModalOpen}
        onClose={() => setIsOccModalOpen(false)}
        occupation={selectedOcc}
      />
      {occupations.length === 0 ? (
        <div className="empty-market-state">
          <Briefcase size={48} />
          <h4>No Market Data Available</h4>
          <p>We couldn't find any regional occupation records for the selected year. Please try again later or check back once the datasets are synchronized.</p>
        </div>
      ) : (
        <div className="insights-grid">
          {/* Left Panel: Highlights & Charts */}
          <div className="insights-main">
            <header className="insights-header">
              <div className="title-area">
                <div className="badge-premium">Live Market Data</div>
                <h3>Regional Workforce Analysis</h3>
                <div className="region-selector-wrapper">
                  <MapPin size={14} className="pin-icon" />
                  <select
                    value={selectedArea}
                    onChange={(e) => setSelectedArea(e.target.value)}
                    className="region-select"
                  >
                    {availableAreas.length === 0 ? (
                      <option value={selectedArea}>{selectedArea || 'Select Region'}</option>
                    ) : (
                      availableAreas.map(area => (
                        <option key={area} value={area}>{area}</option>
                      ))
                    )}
                  </select>
                </div>
                <p>
                  {activeMetric === 'salary'
                    ? `Highest-paying career paths and annual wage benchmarks for ${selectedArea === 'Memphis, TN-MS-AR' ? 'the Memphis Metropolitan Area' : selectedArea}.`
                    : `Occupations with the highest employment volume and labor demand for ${selectedArea === 'Memphis, TN-MS-AR' ? 'the Memphis Metropolitan Area' : selectedArea}.`}
                </p>
              </div>

              <div className="metric-toggle">
                <DataToolTip text="View regional median annual wages across different occupational sectors based on 2025 Bureau of Labor Statistics (BLS) data.">
                  <button
                    className={activeMetric === 'salary' ? 'active' : ''}
                    onClick={() => setActiveMetric('salary')}
                  >
                    <DollarSign size={16} /> Salary
                  </button>
                </DataToolTip>
                <DataToolTip text="Analyze labor market demand based on regional employment volume and occupational availability derived from 2025 Bureau of Labor Statistics (BLS) datasets.">
                  <button
                    className={activeMetric === 'demand' ? 'active' : ''}
                    onClick={() => setActiveMetric('demand')}
                  >
                    <TrendingUp size={16} /> Demand
                  </button>
                </DataToolTip>
              </div>
            </header>

            <div className="market-summary-cards">
              <div className="summary-card clickable-card" onClick={() => setIsRegionalModalOpen(true)}>
                <div className="card-icon"><DollarSign size={20} /></div>
                <div className="card-info">
                  <span className="value">{formatCurrency(data?.medianWage)}</span>
                  <span className="label">Regional Median Wage</span>
                </div>
              </div>
              <div className="summary-card clickable-card" onClick={() => setIsRegionalModalOpen(true)}>
                <div className="card-icon"><Briefcase size={20} /></div>
                <div className="card-info">
                  <span className="value">{formatNumber(data?.totalRecords)}</span>
                  <span className="label">Occupational Records</span>
                </div>
              </div>
              <div className="summary-card clickable-card" onClick={() => setIsRegionalModalOpen(true)}>
                <div className="card-icon"><MapPin size={20} /></div>
                <div className="card-info">
                  <span className="value">{data?.uniqueAreas || 0}</span>
                  <span className="label">Market Sub-sectors</span>
                </div>
              </div>
            </div>

            <div className="chart-container-glass">
              <div className="chart-header">
                <h4>Top Growth Sectors</h4>
                <div className="chart-legend">
                  <span className="dot" style={{ background: '#6366f1' }}></span>
                  {activeMetric === 'salary' ? 'Median Annual Wage' : 'Total Employment'}
                </div>
              </div>
              <div className="chart-scroll-area">
                <div style={{ height: `${Math.max(chartData.length * 45, 500)}px` }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 40, top: 10, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="rgba(255,255,255,0.1)" />
                      <XAxis
                        type="number"
                        hide
                      />
                      <YAxis
                        dataKey="name"
                        type="category"
                        width={180}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fontWeight: 600, fill: '#64748b' }}
                      />
                      <Tooltip
                        cursor={{ fill: 'rgba(99, 102, 241, 0.05)' }}
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const d = payload[0].payload;
                            return (
                              <div className="custom-chart-tooltip">
                                <p className="tooltip-title">{d.fullTitle}</p>
                                <div className="tooltip-stat">
                                  <span className="label">Median Salary:</span>
                                  <span className="value">{formatCurrency(d.salary)}</span>
                                </div>
                                <div className="tooltip-stat">
                                  <span className="label">Regional Jobs:</span>
                                  <span className="value">{formatNumber(d.employment)}</span>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar
                        dataKey={activeMetric === 'salary' ? 'salary' : 'employment'}
                        radius={[0, 4, 4, 0]}
                        barSize={20}
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel: Job List */}
          <div className="insights-sidebar">
            <div className="sidebar-header">
              <h4>Market Explorer</h4>
              <div className="search-box">
                <Search size={14} />
                <input
                  type="text"
                  placeholder="Search roles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="job-list-scroll">
              {occupations.map((job, idx) => (
                <div
                  key={idx}
                  className="job-item-card clickable-card"
                  onClick={() => {
                    console.log('Opening Detail for:', job);
                    setSelectedOcc(job);
                    setIsOccModalOpen(true);
                  }}
                >
                  <div className="job-info">
                    <span className="job-title">{job.occTitle || job.occ_title}</span>
                    <div className="job-meta">
                      <span className="salary">{formatCurrency(job.annualMedianWage || job.annual_median)}</span>
                      <span className="separator">•</span>
                      <span className="demand">{formatNumber(job.totalEmployment || job.tot_emp)} Jobs</span>
                      <span className="separator">•</span>
                      <span className="location">{selectedArea}</span>
                    </div>
                  </div>
                  <div className="job-action">
                    <ArrowRight size={14} />
                  </div>
                </div>
              ))}
            </div>

            <div className="market-footer-note">
              <p>Data source: BLS OEWS 2024-2025 Standard Occupational Classification.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobMarketInsights;
